import {
  Component,
  OnInit,
  Input,
  ViewChild,
  forwardRef,
  Optional,
  Host,
  SkipSelf,
  DoCheck,
  RendererFactory2,
  Renderer2,
  OnDestroy,
  AfterViewInit,
} from "@angular/core";
import { ComponentRendering } from "@sitecore-jss/sitecore-jss-angular";
import {
  CountryListResponse,
  CountryListService,
} from "../../../transfers/services/country-list.service";
import { FormFieldsGeneratorService } from "../../services/form-fields-generator.service";
import { DialogStateService } from "../../../dialogs/services/dialog-state.service";
import {
  UntypedFormGroup,
  UntypedFormControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ControlContainer,
  AbstractControl,
} from "@angular/forms";
import { MatLegacySelect } from "@angular/material/legacy-select";
import { FormAllFieldsValidationService } from "../../services/form-all-fields-validation.service";
import { Subscription } from "rxjs/internal/Subscription";

interface ICountry {
  countryName: string;
  countryCode: string;
  hidden?: boolean;
}

@Component({
  selector: "ab-country-list",
  templateUrl: "./country-list.component.html",
  styleUrls: ["./country-list.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountryListComponent),
      multi: true,
    },
  ],
})
export class CountryListComponent
  implements OnInit, DoCheck, ControlValueAccessor, OnDestroy, AfterViewInit
{
  @Input() rendering: ComponentRendering;
  @ViewChild(MatLegacySelect, { static: true }) matSelect: MatLegacySelect;
  @Input() formControlName: string;
  @Input() hideLabel: boolean;

  public formFields: any;
  public countryList: ICountry[] = [];
  public countryListDropDown: ICountry[] = [];
  public selectedCountryName: string;
  public onTouched;

  private formControl: AbstractControl;
  private prefilledValue: string;
  private renderer: Renderer2;
  private positionChangeSubscription: Subscription;

  public countryForm = new UntypedFormGroup({
    country: new UntypedFormControl(),
  });

  get country() {
    return this.countryForm.get("country");
  }

  constructor(
    @Optional()
    @Host()
    @SkipSelf()
    private controlContainer: ControlContainer,
    private countyListService: CountryListService,
    private formFieldsGeneratorService: FormFieldsGeneratorService,
    private dialogStateService: DialogStateService,
    private formAllFieldsValidationService: FormAllFieldsValidationService,
    rendererFactory: RendererFactory2
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  ngOnInit() {
    this.formControl = this.controlContainer.control.get(this.formControlName);
    this.country.setValidators(this.formControl.validator);
    this.formFields = this.formFieldsGeneratorService.generateFormFields(
      this.rendering
    ) as any;
    this.getCountryList();
  }

  ngAfterViewInit() {
    // this.positionChangeSubscription = this.matSelect.overlayDir.positionChange.subscribe(
    //   () => {
    //     this.setPosition(
    //       this.matSelect._overlayPanelClass.overlayRef.overlayElement,
    //       'left',
    //       7
    //     );
    //     if (this.matSelect.overlayDir.overlayRef.overlayElement.style.top) {
    //       this.setPosition(
    //         this.matSelect.overlayDir.overlayRef.overlayElement,
    //         'top',
    //         31
    //       );
    //     } else {
    //       this.setPosition(
    //         this.matSelect.overlayDir.overlayRef.overlayElement,
    //         'bottom',
    //         33
    //       );
    //     }
    //   }
    // );
  }

  ngOnDestroy() {
    if (this.positionChangeSubscription) {
      this.positionChangeSubscription.unsubscribe();
    }
  }

  ngDoCheck() {
    if (this.formControl.dirty && this.formControl.touched) {
      this.formAllFieldsValidationService.validation(this.countryForm);
    }
  }

  onOpenedChange(value) {
    if (value) {
      this.renderer.addClass(document.body, "menu-opened");
    } else {
      this.renderer.removeClass(document.body, "menu-opened");
    }
  }

  getCountryList() {
    this.countyListService
      .get()
      .subscribe((response) => this.handleResponse(response));
  }

  handleResponse(response: CountryListResponse) {
    const orderArray =
      this.formFields.fields.intCountry.shortListCountries.value
        .toLowerCase()
        .split(", ");
    if (response.list) {
      Object.keys(response.list).forEach((key) => {
        this.countryList.push({
          countryCode: key,
          countryName: response.list[key],
        });

        const index = orderArray.indexOf(key);

        if (index !== -1) {
          this.countryListDropDown[index] = {
            countryCode: key,
            countryName: response.list[key],
          };
        }
      });

      if (this.prefilledValue) {
        this.setPrefilledToForm();
      }
    }
  }

  openDialog() {
    this.matSelect.close();
    this.dialogStateService
      .openDialog("countryList", this.countryList)
      .subscribe((result: ICountry) => {
        if (result) {
          result.hidden = true;
          this.countryListDropDown.push(result);
          this.country.setValue(result);
        }
      });
  }

  writeValue(value) {
    if (!value) {
      this.countryForm.reset(
        {
          country: null,
        },
        {
          emitEvent: false,
        }
      );
    } else {
      this.prefilledValue = value.countryCode
        ? value.countryCode.toLowerCase()
        : value.toLowerCase();

      if (this.countryList && this.countryList.length !== 0) {
        this.setPrefilledToForm();
      } else {
        this.country.setValue(null, { emitEvent: true });
      }
    }
  }

  registerOnChange(fn) {
    this.country.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  public setPosition(
    element: HTMLElement,
    propertyName: string,
    increaseValue: number
  ) {
    let position = 0;
    position = parseFloat(element.style[propertyName].replace("px", ""));

    position = position + increaseValue;

    element.style[propertyName] = `${position}px`;
  }

  private setPrefilledToForm() {
    let prefilledCountry;
    this.countryListDropDown.some((country) => {
      if (country.countryCode === this.prefilledValue) {
        prefilledCountry = country;
        return true;
      }

      return false;
    });

    if (!prefilledCountry) {
      this.countryList.some((country) => {
        if (country.countryCode === this.prefilledValue) {
          prefilledCountry = country;
          return true;
        }

        return false;
      });

      prefilledCountry.hidden = true;
      this.countryListDropDown.push(prefilledCountry);
    }

    setTimeout(() => {
      this.countryForm.setValue({
        country: prefilledCountry,
      });
    });

    this.prefilledValue = null;
  }
}